// ProductFilterDropdown component - Filter by THC/CBD amounts and menu type
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const ProductFilterDropdown = ({ filters, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const filterOptions = {
    stockFilter: [
      { value: 'inStockOnly', label: 'In Stock Only' }
    ],
    thcAmount: [
      { value: '150', label: 'THC 150 MG' },
      { value: '300', label: 'THC 300 MG' },
      { value: '500', label: 'THC 500 MG' },
      { value: '1000', label: 'THC 1000 MG' }
    ],
    cbdAmount: [
      { value: '300', label: 'CBD 300 MG' },
      { value: '500', label: 'CBD 500 MG' }
    ],
    menuType: [
      { value: 'rec', label: 'Recreational' },
      { value: 'medical', label: 'Medical' }
    ]
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters };
    
    if (filterType === 'thcAmount' || filterType === 'cbdAmount') {
      // Toggle the filter value
      if (newFilters[filterType].includes(value)) {
        newFilters[filterType] = newFilters[filterType].filter(v => v !== value);
      } else {
        newFilters[filterType] = [...newFilters[filterType], value];
      }
    } else if (filterType === 'menuType') {
      // Toggle the menu type
      if (newFilters.menuType.includes(value)) {
        newFilters.menuType = newFilters.menuType.filter(v => v !== value);
      } else {
        newFilters.menuType = [...newFilters.menuType, value];
      }
    } else if (filterType === 'stockFilter') {
      // Toggle the stock filter
      if (newFilters.stockFilter.includes(value)) {
        newFilters.stockFilter = newFilters.stockFilter.filter(v => v !== value);
      } else {
        newFilters.stockFilter = [...newFilters.stockFilter, value];
      }
    }
    
    onChange(newFilters);
  };

  const clearAllFilters = () => {
    onChange({
      stockFilter: [],
      thcAmount: [],
      cbdAmount: [],
      menuType: []
    });
  };

  const hasActiveFilters = filters.thcAmount.length > 0 || filters.cbdAmount.length > 0 || filters.menuType.length > 0 || filters.stockFilter.length > 0;
  const activeFilterCount = filters.thcAmount.length + filters.cbdAmount.length + filters.menuType.length + filters.stockFilter.length;

  return (
    <div className="filter-dropdown-container" ref={dropdownRef}>
      <button
        id="product-filters"
        type="button"
        className={`filter-dropdown-toggle ${isOpen ? 'open' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setIsOpen(false);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="product-filter-dropdown-menu"
      >
        <span className="dropdown-text">
          Filters
          {hasActiveFilters && <span className="filter-count">{activeFilterCount}</span>}
        </span>
        <span className="dropdown-arrow">
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div
          id="product-filter-dropdown-menu"
          className="filter-dropdown-menu"
          role="listbox"
          aria-label="Product filters"
          aria-multiselectable="true"
        >
          <div className="dropdown-header">
            {/* <span>Filter Products</span> */}
            {hasActiveFilters && (
              <button type="button" className="clear-filters-btn" onClick={clearAllFilters}>
                Clear all
              </button>
            )}
          </div>
          
          <div className="dropdown-content">
            {/* Stock Filter Section */}
            <div className="filter-category">
              <div className="category-header">Availability</div>
              <div className="filter-options">
                {filterOptions.stockFilter.map((option) => (
                  <button
                    type="button"
                    key={`stock-${option.value}`}
                    className={`filter-option ${filters.stockFilter.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('stockFilter', option.value)}
                    role="option"
                    aria-selected={filters.stockFilter.includes(option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.stockFilter.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Menu Type Section */}
            <div className="filter-category">
              <div className="category-header">Menu Type</div>
              <div className="filter-options">
                {filterOptions.menuType.map((option) => (
                  <button
                    type="button"
                    key={`menu-${option.value}`}
                    className={`filter-option ${filters.menuType.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('menuType', option.value)}
                    role="option"
                    aria-selected={filters.menuType.includes(option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.menuType.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* THC Amount Section */}
            <div className="filter-category">
              <div className="category-header">THC Amount</div>
              <div className="filter-options">
                {filterOptions.thcAmount.map((option) => (
                  <button
                    type="button"
                    key={`thc-${option.value}`}
                    className={`filter-option ${filters.thcAmount.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('thcAmount', option.value)}
                    role="option"
                    aria-selected={filters.thcAmount.includes(option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.thcAmount.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* CBD Amount Section */}
            <div className="filter-category">
              <div className="category-header">CBD Amount</div>
              <div className="filter-options">
                {filterOptions.cbdAmount.map((option) => (
                  <button
                    type="button"
                    key={`cbd-${option.value}`}
                    className={`filter-option ${filters.cbdAmount.includes(option.value) ? 'selected' : ''}`}
                    onClick={() => handleFilterChange('cbdAmount', option.value)}
                    role="option"
                    aria-selected={filters.cbdAmount.includes(option.value)}
                  >
                    <span className="option-label">{option.label}</span>
                    {filters.cbdAmount.includes(option.value) && <span className="selected-indicator">✓</span>}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

ProductFilterDropdown.propTypes = {
  filters: PropTypes.shape({
    thcAmount: PropTypes.array.isRequired,
    cbdAmount: PropTypes.array.isRequired,
    menuType: PropTypes.array.isRequired,
    stockFilter: PropTypes.array.isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired
};

export default ProductFilterDropdown;
